import {
  itemSource,
  type ParticipantId,
  type TimelineItemAny,
  type TimelineItemV3,
} from '../types/renderConfigV2'

/**
 * Items that depend on another item (#541, #566).
 *
 * A follower reads the item it `follows` at render time: captions (its
 * words), a speaker's name or handle text and their avatar picture (its
 * speaker spans), the audio visualizer (its sound). What it follows is any
 * item that plays a source: a sourced scene, or a free video or audio item
 * (a reaction, the second recording of a split view). A view shows the
 * picture of the scene it names in `scene`. This module is the one place that
 * says which items depend on another and which each may depend on. Pure and
 * JSX-free, like everything under `timing/`.
 *
 * `scene` on any other item is membership only (a layout placed it for that
 * scene), never something read.
 */

export type FollowerKind = 'captions' | 'speaker' | 'visualizer' | 'view'

/** What an item needs from its parent, or `null` for an item that reads nothing. */
export function followerKind(item: TimelineItemV3): FollowerKind | null {
  if (item.type === 'view') return 'view'
  if (item.type === 'captions') return 'captions'
  if (item.type === 'text') return item.dynamic ? 'speaker' : null
  if (item.type === 'component') {
    if (item.componentId === 'audio-visualizer') return 'visualizer'
    if (item.componentId === 'picture' && item.props.source === 'speaker')
      return 'speaker'
  }
  return null
}

/**
 * The participant a speaker element is pinned to (#566): a dynamic text's
 * `dynamic.participantId`, a speaker picture's `participantId`. `undefined` for an
 * element that follows whoever speaks, for anything that is not a speaker
 * element (a url picture may carry the key; the validator refuses it), and
 * for every generation-2 item, which has no pin. Takes either generation so
 * `hydrateItem` can read it.
 */
export function pinnedParticipant(
  item: TimelineItemAny,
): ParticipantId | undefined {
  if (item.type === 'text')
    return item.dynamic && 'participantId' in item.dynamic
      ? item.dynamic.participantId
      : undefined
  if (
    item.type === 'component' &&
    item.componentId === 'picture' &&
    item.props.source === 'speaker' &&
    'participantId' in item
  )
    return item.participantId
  return undefined
}

/** The item a follower reads, or the scene a view shows, if it names one. */
export function parentOf(item: TimelineItemV3): string | undefined {
  if (item.type === 'view') return item.scene
  return 'follows' in item ? item.follows : undefined
}

/** The scene an item belongs to, if it names one: a view's, or membership. */
export function sceneOf(item: TimelineItemV3): string | undefined {
  return 'scene' in item ? item.scene : undefined
}

export function withoutScene<T extends TimelineItemV3>(item: T): T {
  if (!('scene' in item) || item.scene === undefined) return item
  const { scene: _scene, ...rest } = item
  void _scene
  return rest as T
}

/**
 * Whether `parent` is one this kind of follower may read. A view shows a
 * scene, so it needs a sourced scene. Words and speakers come from a
 * transcript, so captions and speaker elements need an item that plays a
 * RECORDING; the visualizer analyzes sound, so any source will do, an
 * uploaded file included. A followed item is a scene or a free video or
 * audio item; a title card, an image or another follower has nothing to read.
 */
export function eligibleParent(
  kind: FollowerKind,
  parent: TimelineItemV3 | undefined,
): boolean {
  if (!parent) return false
  if (kind === 'view') return parent.type === 'scene' && !!parent.source
  if (
    parent.type !== 'scene' &&
    parent.type !== 'video' &&
    parent.type !== 'audio'
  )
    return false
  const source = itemSource(parent)
  if (!source) return false
  return kind === 'visualizer' || source.kind === 'recording'
}

/** Whether an item must name a parent (captions, a speaker text, a view) or may name none. */
export function parentRequired(item: TimelineItemV3): boolean {
  return (
    item.type === 'view' ||
    item.type === 'captions' ||
    (item.type === 'text' && !!item.dynamic)
  )
}
